// src/pages/DebtStudio/components/Portfolios/Analysis/PrincipalAllocation.jsx
import React, { useCallback, useMemo } from "react";
import SectionCard from "components/SectionCard";
import dayjs from "lib/dayjs";

import { buildPortfolioDetails } from "pages/DebtStudio/lib/buildPortfolioDetails";

import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from "recharts";

import ChartTooltip from "components/ChartTooltip";
import { makeNiceScaleZeroBased } from "lib/chartScale";

/* ------------------------------------------------------------------
 * Chart helpers
 * ------------------------------------------------------------------ */

const CHART_VARS_STACK = {
  senior: "var(--chart-1)",
  subordinate: "var(--chart-2)",
};

function formatKMB(v) {
  const n = Number(v) || 0;
  const a = Math.abs(n);
  if (a >= 1e9)
    return `${(n / 1e9).toLocaleString(undefined, { maximumFractionDigits: 1 })}B`;
  if (a >= 1e6)
    return `${(n / 1e6).toLocaleString(undefined, { maximumFractionDigits: 1 })}M`;
  if (a >= 1e3)
    return `${(n / 1e3).toLocaleString(undefined, { maximumFractionDigits: 1 })}K`;
  return n.toLocaleString();
}

function formatFull2dp(v, s = "$") {
  return `${s}${(Number(v) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function periodLabel(dateStr) {
  const d = dayjs(dateStr);
  if (!d.isValid()) return dateStr;
  return d.format("YYYY-MM-DD");
}

/* ------------------------------------------------------------------
 * Pie label renderer
 * ------------------------------------------------------------------ */

function makePieLabel(formatAmount) {
  return function PieLabel(props) {
    const { cx, cy, midAngle, innerRadius, outerRadius, percent, value } = props;

    const pct = Number(percent) * 100;
    if (!Number.isFinite(pct) || pct <= 0) return null;
    if (pct < 2) return null;

    const rad = (-Number(midAngle) * Math.PI) / 180;

    // Pull smaller slices a bit farther outward within the wedge so the label has room.
    const sliceIsSmall = pct < 12;
    const positionFactor = sliceIsSmall ? 0.74 : 0.56;

    const r =
      (Number(innerRadius) || 0) +
      ((Number(outerRadius) || 0) - (Number(innerRadius) || 0)) * positionFactor;

    const x = Number(cx) + r * Math.cos(rad);
    const y = Number(cy) + r * Math.sin(rad);

    const line1 = formatAmount(value);
    const line2 = `(${pct.toFixed(1)}%)`;

    // Slightly wider box for smaller slices
    const boxWidth =  80;
    const boxHeight = 42;
    const rx = 6;

    return (
      <g>
        <rect
          x={x - boxWidth / 2}
          y={y - boxHeight / 2}
          width={boxWidth}
          height={boxHeight}
          rx={rx}
          ry={rx}
          fill="hsl(var(--background) / 0.68)"
          stroke="hsl(var(--border) / 0.9)"
          strokeWidth={1}
        />
        <text
          x={x}
          y={y - 6}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={13}
          fontWeight={700}
          fill="hsl(var(--foreground))"
        >
          {line1}
        </text>
        <text
          x={x}
          y={y + 8}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={12}
          fontWeight={600}
          fill="hsl(var(--foreground))"
        >
          {line2}
        </text>
      </g>
    );
  };
}

export default function PrincipalAllocation({
  members = [],
  fiscalYearEndOverride = null,
}) {
  const { principalDueByPriorityFY, composition } = useMemo(() => {
    return buildPortfolioDetails({
      members,
      fiscalYearEndOverride,
    });
  }, [members, fiscalYearEndOverride]);

  const rows = Array.isArray(principalDueByPriorityFY)
    ? principalDueByPriorityFY
    : [];

  const hasData = rows.length > 0;

  const maxTotal = useMemo(() => {
    let m = 0;
    for (const r of rows) m = Math.max(m, Number(r.total) || 0);
    return m;
  }, [rows]);

  const scale = useMemo(() => makeNiceScaleZeroBased(maxTotal, 6, 1), [maxTotal]);
  const yTickFormatter = useCallback((v) => `$${formatKMB(v)}`, []);

  const pieData = useMemo(() => {
    const s = Number(composition?.senior) || 0;
    const sub = Number(composition?.subordinate) || 0;
    return [
      { name: "Senior", value: s, key: "senior" },
      { name: "Subordinate", value: sub, key: "subordinate" },
    ].filter((x) => x.value > 0);
  }, [composition?.senior, composition?.subordinate]);

  const pieLabel = useMemo(() => makePieLabel((v) => `$${formatKMB(v)}`), []);

  return (
    <SectionCard title="Principal Allocation">
      {!hasData ? (
        <div className="p-4 text-muted">No principal data available for the selected issues.</div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Pie */}
            <div className="lg:col-span-4 bg-card border border-border rounded-xl p-4 shadow-sm h-96">
              {pieData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-muted">
                  No principal amounts found.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      cx="50%"
                      cy="50%"
                      outerRadius="90%"
                      isAnimationActive={false}
                      label={pieLabel}
                      labelLine={false}
                    >
                      {pieData.map((p) => (
                        <Cell
                          key={p.key}
                          fill={
                            p.key === "senior"
                              ? CHART_VARS_STACK.senior
                              : CHART_VARS_STACK.subordinate
                          }
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Bar */}
            <div className="lg:col-span-8 bg-card border border-border rounded-xl p-4 shadow-sm h-96">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={rows.map((r) => ({
                    ...r,
                    period: periodLabel(r.date),
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis
                    domain={[scale.niceMin, scale.niceMax]}
                    ticks={scale.ticks}
                    tickFormatter={yTickFormatter}
                  />

                  <Tooltip
                    content={(props) => {
                      const filteredPayload = (props.payload || []).filter((p) => {
                        const v = Number(p?.value) || 0;
                        return Math.abs(v) > 0.005;
                      });

                      if (!filteredPayload.length) return null;

                      return (
                        <ChartTooltip
                          {...props}
                          payload={filteredPayload}
                          formatter={(val, name) => [formatFull2dp(val, "$"), name]}
                        />
                      );
                    }}
                  />

                  <Bar
                    dataKey="subordinate"
                    stackId="a"
                    name="Subordinate"
                    fill={CHART_VARS_STACK.subordinate}
                    isAnimationActive={false}
                  />
                  <Bar
                    dataKey="senior"
                    stackId="a"
                    name="Senior"
                    fill={CHART_VARS_STACK.senior}
                    isAnimationActive={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Legend (shared for BOTH pie + bar) */}
          <div className="flex justify-center items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <span
                className="inline-block h-2.5 w-4 border border-border"
                style={{ background: CHART_VARS_STACK.senior }}
              />
              <span className="text-app">Senior</span>
            </div>

            <div className="flex items-center gap-2">
              <span
                className="inline-block h-2.5 w-4 border border-border"
                style={{ background: CHART_VARS_STACK.subordinate }}
              />
              <span className="text-app">Subordinate</span>
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
}
